import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faBuildingColumns,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { BSL_STATUS } from "../Sidebar/utils/sidebarStorage";
import BslStatusBadge from "./BslStatusBadge";
import ApplyForIbanButton from "./ApplyForIbanButton";
import BankBalanceStatus from "../Common/BankBalanceStatus";
import PendingApprovalState, {
  BSL_APPLY_IBAN_STATE,
  BSL_PENDING_APPROVAL_STATE,
} from "../Common/PendingApprovalState";

const currencyLabels = {
  USD: "US Dollar",
  LBP: "Lebanese Pound",
  EUR: "Euro",
};

function formatAmount(value, currency) {
  const amount = Number(value || 0);

  if (currency === "USD" || currency === "EUR") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  const formattedAmount = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(amount);
  return `${formattedAmount} ${currency === "LBP" ? "L.L" : currency || ""}`.trim();
}

export default function AccountBalanceCard({
  bankId,
  accountNumber,
  bankSubtitle,
  iconSrc,
  iconAlt,
  currency,
  available,
  book,
  held,
  connectionState,
  lastBalanceDate,
  loading = false,
  status = BSL_STATUS.approved,
}) {
  const isBsl = bankId === "bsl";
  const isBlockedState =
    isBsl && [BSL_STATUS.apply, BSL_STATUS.pending].includes(status);
  const isPendingState = isBsl && status === BSL_STATUS.pending;
  const statusContent =
    status === BSL_STATUS.pending
      ? BSL_PENDING_APPROVAL_STATE
      : status === BSL_STATUS.apply
        ? BSL_APPLY_IBAN_STATE
        : null;
  const headerContent = (
    <div className="new-balance-card__header">
      <span className="new-balance-card__icon" aria-hidden="true">
        {iconSrc ? (
          <img src={iconSrc} alt={iconAlt || ""} className="new-balance-card__icon-image" />
        ) : (
          <FontAwesomeIcon icon={isBsl ? faBuildingColumns : faWallet} />
        )}
      </span>
      <div className="new-balance-card__titles">
        <span className="new-balance-card__account">
          {bankId === "bsl" ? "BSL" : "Suyool"} {currency}
          {/* {accountNumber ? ` •••• ${String(accountNumber).slice(-4)}` : ""} */}
        </span>
        <strong className="new-balance-card__currency">
          {currencyLabels[currency] || currency}
        </strong>
        <p>{bankSubtitle}</p>
      </div>
      {isBsl && !isPendingState && status !== BSL_STATUS.approved ? (
        <div className="new-balance-card__badge-wrap">
          <BslStatusBadge status={status} />
        </div>
      ) : null}
    </div>
  );
  const balanceContent = (
    <>
      <div className="new-balance-card__balance">
        {loading ? (
          <span className="new-balance-card__skeleton" />
        ) : (
          <strong>{formatAmount(available, currency)}</strong>
        )}
      </div>

      <div className="new-balance-card__connection">
        {isBsl && status === BSL_STATUS.approved && !loading ? (
          <BankBalanceStatus state={connectionState} lastBalanceDate={lastBalanceDate} />
        ) : null}
      </div>

      <div className="new-balance-card__trend">
        <span className="new-balance-card__trend-badge">
          <FontAwesomeIcon icon={faArrowTrendUp} />
          <span>Available balance</span>
        </span>
      </div>

      <div className="new-balance-card__stats">
        <div>
          <span>Available</span>
          <strong>{formatAmount(available, currency)}</strong>
        </div>
        {/* <div>
          <span>Book</span>
          <strong>{formatAmount(book, currency)}</strong>
        </div>
        <div>
          <span>Held</span>
          <strong>{formatAmount(held, currency)}</strong>
        </div> */}
      </div>
    </>
  );

  const cardContent = (
    <article className="new-balance-card new-balance-card--account">
      {headerContent}
      {balanceContent}
    </article>
  );

  if (!isBlockedState) return cardContent;

  return (
    <PendingApprovalState
      action={status === BSL_STATUS.apply ? <ApplyForIbanButton /> : null}
      className="new-balance-card__pending-state"
      icon={statusContent?.icon}
      message={statusContent?.message}
      title={statusContent?.title}
    >
      {cardContent}
    </PendingApprovalState>
  );
}
